import React from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  Paper,
  TableHead,
  TableRow,
  Box,
  Typography,
  LinearProgress,
} from 'components/mui';
import { DateGroupRow } from 'containers/pages/payrollReport/types';
import {
  guardParentColumns,
  guardChildColumns,
  consultantParentColumns,
  consultantChildColumns,
} from 'containers/pages/payrollReport/data';
import { usePayrollTable } from 'containers/pages/payrollReport/hooks';
import DateRow from './DateRow';

interface PayrollTableProps {
  dateGroups: DateGroupRow[];
  reportType: 'guard' | 'consultant' | '';
  consultantName: string;
  startDate: string | null;
  endDate: string | null;
  grandTotal: string;
  isLoading: boolean;
  hasFetched: boolean;
}

function PayrollTable({
  dateGroups,
  reportType,
  consultantName,
  startDate,
  endDate,
  grandTotal,
  isLoading,
  hasFetched,
}: PayrollTableProps) {
  const { isExpanded, toggleExpand } = usePayrollTable();

  const parentColumns =
    reportType === 'guard' ? guardParentColumns : consultantParentColumns;
  const childColumns =
    reportType === 'guard' ? guardChildColumns : consultantChildColumns;

  const title = `${consultantName} (${startDate || ''} to ${endDate || ''}) - TOTAL HOURS: ${grandTotal || '0h 0m'}`;

  if (isLoading) {
    return (
      <Paper elevation={1} sx={{ p: { xs: 2, sm: 3 } }}>
        <Box sx={{ mb: 2, width: '60%', height: 32, bgcolor: 'action.hover', borderRadius: 1 }} />
        <LinearProgress />
      </Paper>
    );
  }

  return (
    <Paper elevation={1}>
      <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
          {title}
        </Typography>
      </Box>

      <TableContainer sx={{ overflowX: 'auto' }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              {parentColumns.map((col) => (
                <TableCell
                  key={col.id}
                  sx={{ width: col.width, fontWeight: 600 }}
                >
                  {col.label}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>

          <TableBody>
            {hasFetched && dateGroups.length === 0 && (
              <TableRow>
                <TableCell
                  colSpan={parentColumns.length}
                  align="center"
                  sx={{ py: 6 }}
                >
                  <Typography sx={{ color: 'text.disabled', fontStyle: 'italic' }}>
                    No payroll data found for the selected criteria
                  </Typography>
                </TableCell>
              </TableRow>
            )}

            {dateGroups.map((row) => (
              <DateRow
                key={row.id}
                row={row}
                reportType={reportType}
                isExpanded={isExpanded(row.id)}
                onToggle={toggleExpand}
                childColumns={childColumns}
              />
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  );
}

export default PayrollTable;
